"use client";

import { useCallback } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { fetchShopProducts } from "@/lib/community/api-client";
import { getNextCommunityOffset } from "@/lib/community/pagination";
import type { PublicCommunityProduct } from "@/lib/community/types";

export type ShopProductSortBy = "default" | "newest" | "trending";

interface InitialProductData {
  products: PublicCommunityProduct[];
  total: number;
}

export function useShopProducts(
  slug: string,
  initialData?: InitialProductData,
) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const sortBy =
    (searchParams.get("sort") as ShopProductSortBy | null) ?? "default";
  const searchQuery = searchParams.get("q") ?? "";

  const query = useInfiniteQuery({
    queryKey: ["shop-products", slug, sortBy, searchQuery],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await fetchShopProducts(slug, {
        sort_by: sortBy,
        search_query: searchQuery || undefined,
        skip: pageParam,
        take: 20,
      });
      return { ...response, pageParam };
    },
    getNextPageParam: (lastPage) =>
      getNextCommunityOffset(lastPage.pageParam, lastPage.products.length, lastPage.total),
    initialPageParam: 0,
    initialData:
      initialData && sortBy === "default" && !searchQuery
        ? {
            pages: [{ ...initialData, pageParam: 0 }],
            pageParams: [0],
          }
        : undefined,
  });

  const setParams = useCallback(
    (updates: { sort?: ShopProductSortBy; q?: string }) => {
      const next = new URLSearchParams(searchParams);
      if (updates.sort) next.set("sort", updates.sort);
      else next.delete("sort");
      if (updates.q !== undefined) {
        if (updates.q) next.set("q", updates.q);
        else next.delete("q");
      }
      router.replace(`${pathname}?${next.toString()}`, { scroll: false });
    },
    [pathname, router, searchParams],
  );

  return {
    products: query.data?.pages.flatMap((page) => page.products) ?? [],
    total: query.data?.pages[0]?.total ?? 0,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    sortBy,
    searchQuery,
    setParams,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    refetch: query.refetch,
    isFetchNextPageError: query.isFetchNextPageError,
  };
}
